// --- React & Redux
import React from 'react';
import { useSelector } from 'react-redux';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { getItemRange } from 'datastage/utils/pagination';
// --- Hooks & Store
import { useJobMetricActions } from 'datastage/store/jobMetrics/useJobMetrics';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { DetailsModal } from 'datastage/components/DetailsModal';
import { Pagination } from 'datastage/components/Pagination';
import { JobMetricsGrid } from './grid';

/**
 * Job Metrics Modal
 *
 * Features:
 * - Displays a modal with job metrics in a grid
 * - Supports pagination to navigate through metrics
 * - Fetches metrics data from API based on filters and sorting
 *
 * @returns {Element}
 * @constructor
 */
export const JobMetricsModal = () => {
    const {
        isModalOpen,
        filterForm,
        rangeParam,
        sortParam,
        totalPages,
        currentPage,
        itemsPerPage,
    } = useSelector((state) => state.jobMetrics);

    const { updateJobMetrics } = useJobMetricActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();

    // Helper: get page size from rangeParam
    const getPageSize = () => {
        if (Array.isArray(rangeParam) && rangeParam.length === 2) {
            return rangeParam[1] - rangeParam[0];
        }
        return 10;
    };

    const onCloseModal = () => {
        updateJobMetrics({
            isModalOpen: false,
        });
    };

    // Handle page change in pagination
    const handlePageChange = async (e) => {
        const pageNumber = typeof e === 'number' ? e : e?.selected;
        if (pageNumber === currentPage) return;
        setLoadingTrue();
        try {
            const pageSize = getPageSize();
            const nextPageRange = getItemRange(pageNumber, itemsPerPage);
            const data = await datastageApi.jobMetrics(
                filterCriteria(filterForm, '', 'jobMetrics'),
                nextPageRange,
                sortParam,
            );

            if (data?.code) {
                showErrorToast(data?.message || 'Failed to fetch job metrics data.');
                return;
            }
            const total = data?.totalElements || 0;
            updateJobMetrics({
                metrics: data?.jobMetricsListDTO,
                totalPages: Math.ceil(total / pageSize),
                totalItems: total,
                currentPage: pageNumber,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Failed to fetch job metrics data.');
            console.error('Error fetching job metrics:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
       <DetailsModal
           modalHeader={'Job Metrics'}
            isOpen={isModalOpen}
            onClose={onCloseModal}
            footer={
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Pagination
                        pageCount={totalPages}
                        onPageChange={handlePageChange}
                        previousLabel="←"
                        nextLabel="→"
                        forcePage={currentPage}
                    />
                </div>
            }
        >
            <JobMetricsGrid />
        </DetailsModal>
    );
};
